import { Injectable } from '@angular/core';
import { WorkRequest } from '@app-models/work-request';

/**
 * Service to encode/decode WorkRequest data to bypass WAF rule 933210
 * 
 * WAF rule 933210 (PHP Injection: Variable Function Call) blocks requests containing
 * patterns like "(text)" in request body parameters. This service encodes filed in base64
 * before sending to API, then decodes them in responses.
 * 
 * Example:
 *   Input:  "Lamont, Steven (He/Him) (CSCO)"
 *   Encoded: "LTGFtb250LCBTdGV2ZW4gKENTQ08p"
 *   Stored in DB: "Lamont, Steven (He/Him) (CSCO)" (decoded by backend)
 */
@Injectable({
  providedIn: 'root'
})
export class WafEncoderService {

  constructor() { }

  /**
   * Encode a single string value - replace parentheses with base 64 encoding to bypass WAF rule
   * @param value String to encode
   * @returns Encoded string encoding in base64
   */
  private encodeValue(value: string | null | undefined): string | null | undefined {
    if (!value) return value;

    //return value
    //  .replace(/\(/g, '&#40;')  // ( → &#40;
    //  .replace(/\)/g, '&#41;'); // ) → &#41;
    try {
      return btoa(value);
    } catch (e) {
      return value; // If encoding fails, return original value to avoid data loss
    }
  }

  /**
   * Decode a single string value - convert base64 back to parentheses
   * @param value String to decode
   * @returns Decoded string with ( and ) restored
   */
  private decodeValue(value: string | null | undefined): string | null | undefined {
    if (!value) return value;
    return atob(value);
    //return value
    //  .replace(/&#40;/g, '(')
    //  .replace(/&#41;/g, ')');
  }

  /**
   * Encode all text fields in WorkRequest to bypass WAF rule 933210
   * This should be called before sending POST/PUT requests to the API
   * 
   * @param wr WorkRequest object to encode
   * @returns New WorkRequest object with encoded fields
   */
  encodeWorkRequest(wr: WorkRequest): WorkRequest {
    if (!wr) return wr;
    
    const encoded = { ...wr };

    // List of all string fields that might contain parentheses
    const stringFields = [
      'requestorName', 'subject', 'businessRequirements', 
      'desiredOutcome', 'externalInvolvement', 'requestNotes', 
      'businessSponsor', 'notes', 'resolution', 'projectTitle', 
      'projectDescription', 'inScope', 'outOfScope', 'risks', 
      'riskMitigation', 'objectives', 'keyAccomplishments', 
      'upcomingActivities', 'decisions', 'department',
      'phaseInfo', 'sppInfo', 'fundingSource', 'portfolio',
      'projectCategory', 'projectGovernance', 'traPiaSasRating',
      'checkpoint', 'trbCheckpoint', 'registrationReason'
    ];

    // Encode all string fields
    stringFields.forEach(field => {
      if (encoded[field]) {
        encoded[field] = this.encodeValue(encoded[field] as string);
      }
    });

    // Encode array fields with 'name' property
    const arrayFields = ['sponsors', 'applicationOwners', 'requestResources', 'stakeholderResources'];
    
    arrayFields.forEach(field => {
      if (encoded[field] && Array.isArray(encoded[field])) {
        encoded[field] = encoded[field].map((resource: any) => ({
          ...resource,
          name: this.encodeValue(resource.name)
        }));
      }
    });

    // Encode milestone descriptions
    if (encoded.requestMilestones && Array.isArray(encoded.requestMilestones)) {
      encoded.requestMilestones = encoded.requestMilestones.map((m: any) => ({
        ...m,
        description: this.encodeValue(m.description)
      }));
    }

    if (encoded.pmMilestones && Array.isArray(encoded.pmMilestones)) {
      encoded.pmMilestones = encoded.pmMilestones.map((m: any) => ({
        ...m,
        description: this.encodeValue(m.description)
      }));
    }

    return encoded;
  }

  /**
   * Decode WorkRequest (for responses from backend)
   * Note: Backend should return decoded data, but this ensures consistency
   * 
   * @param wr WorkRequest object to decode
   * @returns New WorkRequest object with decoded fields
   */
  decodeWorkRequest(wr: WorkRequest): WorkRequest {
    if (!wr) return wr;
    
    const decoded = { ...wr };

    const stringFields = [
      'requestorName', 'subject', 'businessRequirements', 
      'desiredOutcome', 'externalInvolvement', 'requestNotes', 
      'businessSponsor', 'notes', 'resolution', 'projectTitle', 
      'projectDescription', 'inScope', 'outOfScope', 'risks', 
      'riskMitigation', 'objectives', 'keyAccomplishments', 
      'upcomingActivities', 'decisions', 'department',
      'phaseInfo', 'sppInfo', 'fundingSource', 'portfolio',
      'projectCategory', 'projectGovernance', 'traPiaSasRating',
      'checkpoint', 'trbCheckpoint', 'registrationReason'
    ];

    stringFields.forEach(field => {
      if (decoded[field]) {
        decoded[field] = this.decodeValue(decoded[field] as string);
      }
    });

    const arrayFields = ['sponsors', 'applicationOwners', 'requestResources', 'stakeholderResources'];
    
    arrayFields.forEach(field => {
      if (decoded[field] && Array.isArray(decoded[field])) {
        decoded[field] = decoded[field].map((resource: any) => ({
          ...resource,
          name: this.decodeValue(resource.name)
        }));
      }
    });

    if (decoded.requestMilestones && Array.isArray(decoded.requestMilestones)) {
      decoded.requestMilestones = decoded.requestMilestones.map((m: any) => ({
        ...m,
        description: this.decodeValue(m.description)
      }));
    }

    if (decoded.pmMilestones && Array.isArray(decoded.pmMilestones)) {
      decoded.pmMilestones = decoded.pmMilestones.map((m: any) => ({
        ...m,
        description: this.decodeValue(m.description)
      }));
    }

    return decoded;
  }
}
